/**
 * Route Loader Helpers
 * Utility functions for route data loading
 */

import { redirect, type LoaderFunctionArgs } from 'react-router-dom';
import { ENV } from '@/config/env';
import {
  getStoredCredentials,
  isAccessTokenValid,
  isRefreshTokenValid,
  refreshToken,
  clearAuthStorage,
  getRememberMe,
} from '@/services/keycloakAuthService';
import { SecureStorage } from '@/utils/security';

/**
 * Type for loader data that can be returned
 */
export type LoaderData<T> = {
  data: T | null;
  error: Error | null;
  isLoading: boolean;
};

/**
 * Helper to create a loader data response
 */
export function createLoaderData<T>(
  data: T | null = null,
  error: Error | null = null,
  isLoading: boolean = false
): LoaderData<T> {
  return { data, error, isLoading };
}

/**
 * Helper to check authentication status
 * Redirects to login if not authenticated
 */
export async function requireAuth(request: Request): Promise<boolean> {
  // If auth guard is disabled, allow access (development mode)
  if (!ENV.AUTH_GUARD) {
    return true;
  }

  const redirectToLogin = () => {
    const url = new URL(request.url);
    throw redirect(`/login?redirect=${encodeURIComponent(url.pathname)}`);
  };

  // Browser-close detection: if "remember me" was unchecked and
  // sessionStorage marker is missing, the browser was closed since login
  const rememberMe = await getRememberMe();
  if (!rememberMe && !sessionStorage.getItem('session_alive')) {
    clearAuthStorage();
    redirectToLogin();
  }

  // Check if user is authenticated using SecureStorage (AES-256-GCM encrypted)
  const credentials = await getStoredCredentials();

  if (!credentials?.access_token || !credentials.refresh_token) {
    redirectToLogin();
  }

  // Access token still valid — allow access
  if (await isAccessTokenValid()) {
    return true;
  }

  // Access token expired — check if refresh token is still valid
  if (!(await isRefreshTokenValid())) {
    // Both tokens expired — clear storage and redirect
    clearAuthStorage();
    redirectToLogin();
  }

  // Refresh token valid — attempt silent refresh
  try {
    const response = await refreshToken(credentials!.refresh_token);
    if (response.success) {
      const { credentials: newCreds } = response.data;
      const now = Math.floor(Date.now() / 1000);
      await SecureStorage.setItem(
        'auth_credentials',
        JSON.stringify({
          ...newCreds,
          access_token_expires_at: now + newCreds.expires_in,
          refresh_token_expires_at: now + newCreds.refresh_expires_in,
        })
      );
      return true;
    }
  } catch {
    // Refresh failed — clear and redirect
  }

  clearAuthStorage();
  redirectToLogin();
  return true; // unreachable, but satisfies TS
}

/**
 * Helper to get query parameters from loader args
 */
export function getSearchParams(args: LoaderFunctionArgs): URLSearchParams {
  const url = new URL(args.request.url);
  return url.searchParams;
}

/**
 * Helper to get route params
 */
export function getParams(args: LoaderFunctionArgs) {
  return args.params;
}

/**
 * Helper to handle loader errors
 */
export function handleLoaderError(error: unknown): LoaderData<null> {
  console.error('Loader error:', error);

  const err = error instanceof Error ? error : new Error('An unknown error occurred');

  return createLoaderData(null, err, false);
}

/**
 * Helper to create a successful loader response
 */
export function loaderSuccess<T>(data: T): LoaderData<T> {
  return createLoaderData(data, null, false);
}

/**
 * Helper to defer loader data (for streaming/suspense)
 */
export async function deferLoaderData<T>(promise: Promise<T>): Promise<LoaderData<T>> {
  try {
    const data = await promise;
    return loaderSuccess(data);
  } catch (error) {
    return handleLoaderError(error) as LoaderData<T>;
  }
}

/**
 * Helper to cache loader data
 * Simple in-memory cache with TTL
 */
class LoaderCache {
  private cache = new Map<string, { data: unknown; expiresAt: number }>();

  set<T>(key: string, data: T, ttlMs: number = 60000): void {
    this.cache.set(key, {
      data,
      expiresAt: Date.now() + ttlMs,
    });
  }

  get<T>(key: string): T | null {
    const cached = this.cache.get(key);

    if (!cached) return null;

    if (Date.now() > cached.expiresAt) {
      this.cache.delete(key);
      return null;
    }

    return cached.data as T;
  }

  clear(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }
}

export const loaderCache = new LoaderCache();
